/**
 * Detection / Playbooks - Phase 4 wiring.
 *
 * Live data source: GET /v1/detection/playbooks
 * Run action: POST /v1/detection/playbooks/:name/run (with empty context
 * for now; future iterations bind the playbook run to a specific decision
 * or actor via the picker).
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Workflow, Play, KeyRound, ShieldOff, Flame, FileText, AlertTriangle, Search, Loader2, CheckCircle2,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { listDetectionPlaybooks, type DetectionPlaybook } from '@/lib/api';
import { refusalText, runCataloguePlaybook, type PlaybookRun } from '@/lib/detection-response-api';

function iconFor(name: string): React.ComponentType<{ className?: string }> {
  if (/section_9|section-9/.test(name)) return ShieldOff;
  if (/aadhaar|vault/.test(name)) return KeyRound;
  if (/cross_border|cross-border|geofence/.test(name)) return ShieldOff;
  if (/breach|dpb/.test(name)) return FileText;
  if (/forest_fire|forest-fire|quarantine|contain/.test(name)) return Flame;
  if (/session|kill/.test(name)) return AlertTriangle;
  if (/investigate|evidence|forensic/.test(name)) return Search;
  return Workflow;
}

/** Plain words for a run's status (Q31: a step is done only when it acted). */
const RUN_STATUS: Record<PlaybookRun['status'], string> = {
  pending_approval: 'waiting for a second approver (a different person, within 30 minutes)',
  running: 'running',
  succeeded: 'every step done',
  awaiting_manual: 'done where it could act; manual steps are waiting for a person',
  failed: 'a step could not act',
  cancelled: 'cancelled',
};

export function DetectionPlaybooks() {
  const [playbooks, setPlaybooks] = useState<DetectionPlaybook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<PlaybookRun | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const refresh = (signal?: AbortSignal) =>
    listDetectionPlaybooks()
      .then((r) => !signal?.aborted && setPlaybooks(r.playbooks))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  async function onRun(p: DetectionPlaybook) {
    setRunning(p.name);
    setRunError(null);
    setLastRun(null);
    try {
      const r = await runCataloguePlaybook(p.name);
      setLastRun(r.run);
    } catch (err) {
      setRunError(refusalText(err));
    } finally {
      setRunning(null);
    }
  }

  const twoPCount = useMemo(() => playbooks.filter((p) => p.requiresTwoPersonRule).length, [playbooks]);
  const builtInCount = useMemo(() => playbooks.filter((p) => p.builtIn).length, [playbooks]);
  // Silence unused-var warnings for builtInCount until the page renders it.
  void builtInCount;

  return (
    <PageContainer>
      <PageHeader
        title="Playbooks"
        subtitle="Pre-approved response runbooks. Banking 2P-marked playbooks require a second distinct approver before execution."
        badges={[
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [
                  { label: `${playbooks.length} playbooks`, variant: 'glass' as const },
                  { label: `${twoPCount} require 2P`, variant: 'warning' as const },
                ]),
        ]}
      />

      {loading && (
        <LoadingState caption="Loading playbook catalogue…" />
      )}
      {error && !loading && (
        <ErrorState
          title="Failed to load playbooks"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {(lastRun || runError) && (
        <div className={`glass-lite border rounded-md p-3 mb-3 text-xs flex items-start gap-2 ${runError ? 'border-sev-high/40' : 'border-success/40'}`}>
          {runError ? <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />}
          <div className="flex-1">
            {runError ? (
              <>
                <div className="font-medium">Playbook run failed</div>
                <div className="text-muted-foreground mt-0.5">{runError}</div>
              </>
            ) : lastRun ? (
              <>
                <div className="font-medium">Playbook "{lastRun.playbookName}": {RUN_STATUS[lastRun.status] ?? lastRun.status}</div>
                <div className="text-muted-foreground mt-0.5">
                  Run ID: <code className="font-mono">{lastRun.id}</code>. Run a playbook from an incident to act on its principals; from here it has none.
                </div>
                {lastRun.stepsCompleted.length > 0 && (
                  <ul className="text-[10px] text-muted-foreground mt-1 ml-4 list-disc">
                    {lastRun.stepsCompleted.slice(0, 8).map((a, i) => (
                      <li key={i}>{a.step.type}: {a.status === 'done' ? 'done' : a.status === 'manual' ? 'manual step' : 'could not act'}. {a.message}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
          <Button size="sm" variant="glass" onClick={() => { setLastRun(null); setRunError(null); }}>Dismiss</Button>
        </div>
      )}

      {!loading && (
        playbooks.length === 0 ? (
          <GlassCard className="p-8 text-center">
            <Workflow className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <CardTitle className="text-base">No playbooks configured yet</CardTitle>
            <CardDescription className="text-sm mt-1">
              Built-in playbooks ship with every tenant; if you're seeing nothing here, the demo seed hasn't been run for this tenant.
            </CardDescription>
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {playbooks.map((p, i) => {
              const Icon = iconFor(p.name);
              const isRunning = running === p.name;
              return (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.4) }}
                >
                  <GlassCard shine className="p-5 h-full">
                    <div className="flex items-start justify-between mb-3">
                      <div className="h-10 w-10 rounded-md bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      {p.requiresTwoPersonRule && <Badge variant="warning" className="text-[9px]">2P</Badge>}
                    </div>
                    <CardTitle className="text-sm">{p.name}</CardTitle>
                    <CardDescription className="text-xs mt-1 mb-3">{p.description}</CardDescription>
                    <div className="space-y-1.5 mb-3">
                      {p.steps.map((s, j) => (
                        <div key={j} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                          <span className="h-4 w-4 rounded-full bg-muted text-foreground flex items-center justify-center text-[9px] shrink-0">{j + 1}</span>
                          <span>
                            <strong className="text-foreground">{s.kind}</strong>: {s.description}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t border-border/30 pt-2 mb-2">
                      <span>{p.builtIn ? 'Built-in' : 'Tenant'}</span>
                      {p.requiresTwoPersonRule && <span>2P approval required</span>}
                    </div>
                    <Button
                      variant="glass"
                      size="sm"
                      className="w-full"
                      onClick={() => onRun(p)}
                      disabled={isRunning}
                      data-action="disc.ddr_playbooks.a01"
                    >
                      {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                      {isRunning ? 'Running…' : p.requiresTwoPersonRule ? 'Run (needs 2P)' : 'Run playbook'}
                    </Button>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        )
      )}

      {/* Wave 5 Group-A (2026-05-27) - auto-fire reference card. The
          AUTO_FIRE_PLAYBOOK_FOR map in detection/service.ts decides
          which playbook fires the moment a decision is recorded. The
          values are short - surface them inline so operators know
          what's automatic vs what they need to trigger manually. */}
      <div className="mt-6">
        <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
          Auto-fire mapping (production behaviour)
        </h2>
        <div className="glass-lite rounded-md p-4 text-xs">
          <p className="text-muted-foreground mb-3 leading-relaxed">
            The Decision Gateway auto-fires these playbooks the moment a decision is recorded.
            All other decision classes require an operator to invoke the playbook explicitly
            from the cards above.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-2 w-2 rounded-full bg-destructive" />
              <span className="font-mono text-[11px]">decision = <strong>block</strong></span>
              <span className="text-muted-foreground">→</span>
              <code className="font-mono text-[11px] text-primary">breach_console_pre_fill</code>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-2 w-2 rounded-full bg-warning" />
              <span className="font-mono text-[11px]">decision = <strong>alert</strong></span>
              <span className="text-muted-foreground">→</span>
              <code className="font-mono text-[11px] text-primary">breach_console_pre_fill</code>
            </div>
          </div>
          <p className="mt-3 text-[10px] text-muted-foreground/80">
            Source:{' '}
            <code className="font-mono">apps/api-controller/src/modules/detection/service.ts</code>{' '}
            (constant <code className="font-mono">AUTO_FIRE_PLAYBOOK_FOR</code>).
          </p>
        </div>
      </div>
    </PageContainer>
  );
}
